/**
 * API Service
 * 
 * Simplified API service that handles all frontend API communication.
 * Always uses the FastAPI backend service for real data and business logic.
 */

import { logger } from './logger';

export interface APIResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
  details?: string;
  timestamp?: string;
}

export interface ScenarioSummary {
  name: string;
  displayName: string;
}

export interface ScenariosResponse {
  success: boolean;
  scenarios: ScenarioSummary[];
  count: number;
}

export interface ScenarioOverview {
  name: string;
  displayName: string;
  metadata: Record<string, unknown>;
  extractedAt?: string;
  availableData: Record<string, unknown>;
}

export interface ScenarioOverviewResponse {
  success: boolean;
  scenario: ScenarioOverview;
  links: Record<string, string>;
}

export interface DSATableRow {
  indicator: string;
  code: string;
  average2018_2021?: number;
  actual: Record<string, number>;
  projections: Record<string, number>;
  cumulative?: number;
  description?: string;
}

export interface ProcessedTable {
  title: string;
  processedData: DSATableRow[];
  summary: {
    totalRows: number;
    hasProjections: boolean;
    lastUpdated: string;
  };
}

export interface ScenarioTableResponse {
  success: boolean;
  scenario: string;
  table: ProcessedTable;
  metadata: Record<string, unknown>;
}

export interface ChartDataPoint {
  year: number;
  value: number;
  scenario?: string;
}

export interface ChartData {
  title: string;
  data: ChartDataPoint[];
  metadata: {
    chartType: string;
    yAxisLabel: string;
    xAxisLabel: string;
  };
}

export interface ComparisonChartData {
  years: number[];
  scenarios: string[];
  data: {
    [scenario: string]: {
      [year: string | number]: number;
    };
  };
}

export interface AllComparisonChartsResponse {
  success: boolean;
  charts: Record<string, ComparisonChartData>;
  metadata: Record<string, unknown>;
}

// Simplified schema matching business rules output
// Note: Percentile fields use ordinal naming convention (1st_percentile, 2nd_percentile, etc.)
// These represent the 1st, 2nd, 3rd, 4th, 5th percentile values (regardless of actual percentile values like 20, 35, 50, etc.)
export interface FanChartSummaryRow {
  Year: number;
  '1st_percentile': number;
  '2nd_percentile': number;
  '3rd_percentile': number;
  '4th_percentile': number;
  '5th_percentile': number;
}

export interface StochasticProjectionResponse {
  summary_rows: FanChartSummaryRow[];
  mu_dict: { g: number; r: number; pb: number };
  sigma_matrix: number[][];
  traces: {
    historical_debt: number[];
    baseline_debt_proj: number[];
    fan_years: number[];
    hist_years: number[];
    sim_years: number[];
  };
  baseline: Array<Record<string, number | string>>; // Baseline GDP table
  parameters: Record<string, unknown>;
  metadata?: Record<string, unknown>;
}


export interface GlobalSettingsData {
  state: string;
  historicalYearStart: number;
  historicalYearEnd: number;
  firstYearProjections: number;
  projectionYears: number;
  mediumTermYear: number;
  debtTarget: number;
  shockInputs: {
    revenueShockType: 'Stdev' | 'Manual';
    revenueShockStdev: number;
    revenueShockManual: number;
    revenueShockDuration: number;
    intergovernmentalRevenueShockType: 'Stdev' | 'Manual';
    intergovernmentalRevenueShockStdev: number;
    intergovernmentalRevenueShockManual: number;
    intergovernmentalRevenueShockDuration: number;
    expenditureShockType: 'Stdev' | 'Manual';
    expenditureShockStdev: number;
    expenditureShockManual: number;
    expenditureShockDuration: number;
    interestRateShockBps: number;
  };
  borrowingInstruments: Array<{
    id: number;
    instrument: string;
    currency: string;
    interestRate: number;
    maturity: number;
    type: string;
    share: number;
  }>;
  stateMetadata: {
    stateName: string;
    debtRating: string;
    ratingAgency: string;
    lastUpdated: string;
    description: string;
  };
  timePeriodConstraints?: {
    projectionYearsMin: number;
    projectionYearsMax: number;
    historicalBeginMin: number;
    historicalBeginMax: number;
    historicalEndMin: number;
    historicalEndMax: number;
  };
}

export interface GlobalSettingsResponse {
  success: boolean;
  settings: GlobalSettingsData;
  recalculationNeeded?: Record<string, boolean>;
  timestamp: string;
}

/**
 * Simplified API Service
 * 
 * Handles all API communication with the FastAPI backend service.
 * Always uses real data and business logic from the backend.
 */
export class APIService {
  private baseUrl: string;

  constructor() {
    // Always use relative paths - works in both development and production
    // - Local dev: Next.js rewrites proxy /api/* to http://localhost:8000/api/*
    // - Production (Kubernetes): Ingress routes /api to backend-service:8000
    // This approach works without any environment variables and is deployment-agnostic
    this.baseUrl = '/api';
  }

  /**
   * Get the current service type for debugging/display purposes
   */
  getServiceType(): 'backend' {
    return 'backend';
  }

  /**
   * Get the current base URL for debugging/display purposes
   */
  getBaseUrl(): string {
    return this.baseUrl;
  }

  /**
   * Get tour completion state for the current user
   * @param accessToken - Optional Auth0 access token for authentication
   */
  async getTourCompletion(accessToken?: string): Promise<{ completedTours: string[]; activeTour: string | null }> {
    const data = await this.makeRequest<{ tours: { completedTours: string[]; activeTour: string | null } }>('/tours', {}, accessToken);
    return data.tours;
  }

  /**
   * Update tour completion state for the current user
   * @param update - Tour completion update data
   * @param accessToken - Optional Auth0 access token for authentication
   */
  async updateTourCompletion(update: { completedTours?: string[]; activeTour?: string | null }, accessToken?: string): Promise<{ completedTours: string[]; activeTour: string | null }> {
    const data = await this.makeRequest<{ tours: { completedTours: string[]; activeTour: string | null } }>('/tours', {
      method: 'PUT',
      body: JSON.stringify(update),
    }, accessToken);
    return data.tours;
  }

  /**
   * Make a request to the API
   * @param endpoint - API endpoint path
   * @param options - Fetch options
   * @param accessToken - Optional Auth0 access token for authentication
   */
  async makeRequest<T>(endpoint: string, options: RequestInit = {}, accessToken?: string): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string> || {}),
    };

    // Add Authorization header if access token is provided
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }
    
    const defaultOptions: RequestInit = {
      headers,
      // Include credentials to ensure cookies are sent with requests
      credentials: 'same-origin',
    };

    const response = await fetch(url, { ...defaultOptions, ...options });
    
    // Note: fetch() automatically throws AbortError if signal is aborted
    // This will be caught by the caller's error handling
    
    if (!response.ok) {
      throw new Error(`API request failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    
    // No longer check for success field - use HTTP status codes instead
    return data;
  }

  /**
   * Get all available scenarios
   */
  async getScenarios(): Promise<ScenarioSummary[]> {
    const data = await this.makeRequest<ScenariosResponse>('/scenarios');
    return data.scenarios;
  }

  /**
   * Get scenario overview and metadata
   */
  async getScenario(scenario: string): Promise<ScenarioOverview> {
    const data = await this.makeRequest<ScenarioOverviewResponse>(`/scenarios/${scenario}`);
    return data.scenario;
  }

  /**
   * Get scenario data with optional filtering
   */
  async getScenarioData(
    scenario: string, 
    options: {
      type?: 'tables' | 'charts' | 'fanchart' | 'dsa';
      table?: string;
    } = {}
  ): Promise<unknown> {
    const params = new URLSearchParams();
    
    if (options.type) {
      params.set('type', options.type);
    }
    
    if (options.table) {
      params.set('table', options.table);
    }

    const queryString = params.toString();
    const endpoint = `/scenarios/${scenario}${queryString ? `?${queryString}` : ''}`;
    
    return this.makeRequest(endpoint);
  }

  /**
   * Get scenario as DSA data
   */
  async getScenarioAsDSAData(scenario: string): Promise<unknown> {
    return this.getScenarioData(scenario, { type: 'dsa' });
  }

  /**
   * Get scenario tables
   */
  async getScenarioTables(scenario: string): Promise<ProcessedTable> {
    const data = await this.makeRequest<ScenarioTableResponse>(`/scenarios/${scenario}/tables`);
    return data.table;
  }

  /**
   * Get scenario charts
   */
  async getScenarioCharts(scenario: string): Promise<unknown> {
    return this.getScenarioData(scenario, { type: 'charts' });
  }

  /**
   * Get scenario fan chart
   */
  async getScenarioFanChart(scenario: string): Promise<unknown> {
    return this.getScenarioData(scenario, { type: 'fanchart' });
  }

  /**
   * Get chart data
   */
  async getChartData(chartType: string): Promise<ComparisonChartData | undefined> {
    const charts = await this.getComparisonCharts();
    // getComparisonCharts() already normalizes keys to hyphens, so use chartType directly
    // chartType is already in hyphenated format (e.g., 'debt-to-gdp')
    const chartData = charts[chartType];
    if (!chartData) {
      logger.warn(`Chart data not found for type: ${chartType}. Available keys:`, Object.keys(charts));
    }
    return chartData;
  }

  /**
   * Get scenario analysis
   */
  async getScenarioAnalysis(parameters: Record<string, unknown>): Promise<unknown> {
    return this.makeRequest('/scenarios/analysis', {
      method: 'POST',
      body: JSON.stringify(parameters),
    });
  }

  /**
   * Get default stochastic projection
   */
  async getStochasticProjection(): Promise<StochasticProjectionResponse> {
    return this.makeRequest<StochasticProjectionResponse>('/stochastic-projection');
  }

  /**
   * Run custom stochastic projection
   */
  async runStochasticProjection(parameters: Record<string, unknown>): Promise<StochasticProjectionResponse> {
    return this.makeRequest<StochasticProjectionResponse>('/stochastic-projection', {
      method: 'POST',
      body: JSON.stringify(parameters),
    });
  }


  /**
   * Get global settings
   */
  async getGlobalSettings(): Promise<GlobalSettingsData> {
    const data = await this.makeRequest<GlobalSettingsResponse>('/settings');
    return data.settings;
  }

  /**
   * Update global settings
   */
  async updateGlobalSettings(settings: Partial<GlobalSettingsData>, currentModule?: string): Promise<GlobalSettingsData & { recalculationNeeded?: Record<string, boolean> }> {
    const data = await this.makeRequest<GlobalSettingsResponse>('/settings', {
      method: 'PUT',
      body: JSON.stringify({ settings, currentModule }),
    });
    return { ...data.settings, recalculationNeeded: data.recalculationNeeded };
  }

  /**
   * Reset global settings to defaults
   */
  async resetGlobalSettings(currentModule?: string): Promise<GlobalSettingsData & { recalculationNeeded?: Record<string, boolean> }> {
    const data = await this.makeRequest<GlobalSettingsResponse>('/settings', {
      method: 'POST',
      body: JSON.stringify({ action: 'reset', current_module: currentModule }),
    });
    return { ...data.settings, recalculationNeeded: data.recalculationNeeded };
  }

  /**
   * Get comparison charts
   */
  async getComparisonCharts(): Promise<Record<string, ComparisonChartData>> {
    const response = await this.makeRequest<{ charts?: Record<string, ComparisonChartData> } | Record<string, ComparisonChartData>>('/scenarios/compare/charts');
    // Handle both wrapped response (with success/charts) and direct charts object
    const charts = response.charts || response || {};
    // Convert underscore keys to hyphenated keys to match frontend expectations
    const normalizedCharts: Record<string, ComparisonChartData> = {};
    for (const [key, value] of Object.entries(charts)) {
      const normalizedKey = key.replace(/_/g, '-');
      normalizedCharts[normalizedKey] = value as ComparisonChartData;
    }
    return normalizedCharts;
  }


}

// Export singleton instance
export const apiService = new APIService();

// Export convenience function for backward compatibility
export function getService(): APIService {
  return apiService;
}

// Export service type for configuration
export type ServiceType = 'backend';

// Export function to get current service type
export function getServiceType(): ServiceType {
  return apiService.getServiceType();
}

// Export function to check if using backend service (always true now)
export function isUsingBackendService(): boolean {
  return true;
}

// Export function to check if using API service (always false now)
export function isUsingAPIService(): boolean {
  return false;
}
